/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'clsx';
import { shape, string, number, bool, func } from 'prop-types';

import p from 'polyglot/polyglot';

import { getWebPImage } from 'utils/image';

import {
  MAKE_DEFAULT_PICTURE_SUCCESS,
  makeDefaultPicture as makeDefaultPictureAction,
  deletePicture as deletePictureAction,
} from 'actions/user';

import Spinner from 'components/Spinner/Spinner';

import FileInput from '../FileInput/FileInput';

import PenIcon from 'gfx/vector/pen-icon.svg';
import EyeIcon from 'gfx/vector/eye-icon.svg';

import styles from './picture.scss';

const isTouch =
  'ontouchstart' in window ||
  (window.DocumentTouch && document instanceof window.DocumentTouch);

/**
 * This is the Picture component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class Picture extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {Object}
   */
  static propTypes = {
    auth: shape({
      token: string.isRequired,
    }).isRequired,
    user: shape({
      type: string.isRequired,
    }).isRequired,
    index: number.isRequired,
    is_default: bool.isRequired,
    medium_url: string.isRequired,
    picture_id: string.isRequired,
    makeDefaultPicture: func.isRequired,
    deletePicture: func.isRequired,
    onClick: func,
  };

  /**
   * Declare default props.
   *
   * @type {Object}
   */
  static defaultProps = {
    onClick: () => {},
  };

  /**
   * Create Picture.
   */
  constructor(props) {
    super(props);

    this.state = {
      sending: false,
      error: null,
    };

    this.wrapper = React.createRef();
  }

  onChange = () => {
    this.setState({
      sending: true,
    });
  };

  onError = error => {
    console.log(error);

    this.setState({
      sending: false,
      error: true,
    });
  };

  onDeletePicture = event => {
    event.preventDefault();
    const {
      auth: { token },
      user,
      index,
      deletePicture,
    } = this.props;

    this.setState({
      sending: true,
    });

    deletePicture(token, index, user.type, {
      index: index,
    });
  };

  onMakeDefaultPicture = event => {
    event.preventDefault();

    const {
      auth: { token },
      user,
      index,
      makeDefaultPicture,
    } = this.props;

    this.setState({ sending: true });

    makeDefaultPicture(token, index, user.type, {
      index: index,
    }).then(makeDefaultPictureAction => {
      if (makeDefaultPictureAction.type === MAKE_DEFAULT_PICTURE_SUCCESS) {
        this.setState({
          sending: false,
        });
      }
    });
  };

  onBlur = event => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      this.wrapper.current.classList.remove(styles.open);
    }
  };

  onToggleOptions = event => {
    if (!isTouch) return;

    event.preventDefault();
    this.wrapper.current.classList.add(styles.open);
  };

  onClickImage = event => {
    if (isTouch) return;

    const { index, onClick } = this.props;

    event.preventDefault();

    onClick(index);
  };

  /**
   * Render Upload.
   *
   * @return {Object}
   */
  render() {
    const { index, is_default, medium_url, picture_id } = this.props;

    const { sending } = this.state;

    return (
      <div
        className={classNames(styles.picture, {
          [styles.default]: is_default,
          [styles.touch]: isTouch,
        })}
        style={
          sending ? {} : { backgroundImage: `url(${getWebPImage(medium_url)})` }
        }
        ref={this.wrapper}
        tabIndex="0"
        onBlur={this.onBlur}
        onClick={this.onToggleOptions}
      >
        {!sending && isTouch && (
          <div className={styles.penIcon}>
            <PenIcon />
          </div>
        )}

        {sending && <Spinner medium />}

        {!sending && (
          <button
            className={styles.openModalButton}
            onClick={this.onClickImage}
          />
        )}

        {!sending && (
          <div className={styles.controls}>
            {!is_default && (
              <button
                className={styles.makeDefaultPictureButton}
                onClick={this.onMakeDefaultPicture}
              >
                {p.t('portfolio.actions.make_profile')}
              </button>
            )}
            <FileInput
              id={picture_id}
              className={styles.changePictureButton}
              index={index}
              sending={sending}
              onChange={this.onChange}
              onError={this.onError}
            >
              <span>{p.t('portfolio.actions.change')}</span>
            </FileInput>
            {!is_default && (
              <button
                className={styles.deletePictureButton}
                onClick={this.onDeletePicture}
              >
                {p.t('portfolio.actions.delete')}
              </button>
            )}

            {!isTouch && (
              <div className={styles.eyeIcon}>
                <EyeIcon />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default connect(
  ({ auth, user }) => ({ auth, user }),
  {
    makeDefaultPicture: makeDefaultPictureAction,
    deletePicture: deletePictureAction,
  }
)(Picture);
